// SPDX-License-Identifier: AGPL-3.0-or-later

import AppStorage from '@app/features/platform/state/PersistentStorage';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import UserSettings from '@app/features/user/state/UserSettings';
import type {SyncedPreferences} from '@app/vendor/schema/domains/user/SyncedPreferencesCodec';
import {verifyRoundtripStability} from '@app/features/user/state/SyncedFieldRoundtrip';
import {create, equals, type Message, type MessageInitShape, toBinary} from '@bufbuild/protobuf';
import type {GenMessage} from '@bufbuild/protobuf/codegenv2';
import {reaction, runInAction, when} from 'mobx';

export {verifyRoundtripStability};

const logger = new Logger('SyncedField');
const DEFAULT_DEBOUNCE_MS = 800;
const DEFAULT_MAX_ENCODED_BYTES = 65_536;
const RECENT_LOCAL_MUTATION_WINDOW_MS = 5_000;
const FAILING_BACKOFF_MIN_MS = 30_000;
const FAILING_BACKOFF_MAX_MS = 600_000;
const FAILING_THRESHOLD = 3;
const MIGRATION_FLAG_PREFIX = 'serverSync:migrated';
const DIRTY_FLAG_PREFIX = 'serverSync:dirty';

export type SyncedPreferencesField = Exclude<keyof SyncedPreferences, '$typeName' | '$unknown' | 'sanitizeUrls'>;

export interface SyncedFieldConfig<
	T extends object,
	F extends SyncedPreferencesField,
	M extends Message & NonNullable<SyncedPreferences[F]>,
> {
	readonly field: F;
	readonly schema: GenMessage<M>;
	readonly persist: ReadonlyArray<keyof T>;
	toMessage(store: T): MessageInitShape<GenMessage<M>>;
	applyMessage(store: T, message: M): void;
	readonly version?: number;
	readonly debounceMs?: number;
	readonly syncAcrossTabs?: boolean;
	readonly maxEncodedBytes?: number;
	enabled?(): boolean;
	mergeForMigration?(local: M, server: M): MessageInitShape<GenMessage<M>>;
}

function migrationFlagKey(storeKey: string, field: string): string {
	return `${MIGRATION_FLAG_PREFIX}:${storeKey}:${field}`;
}
function isMigrated(storeKey: string, field: string): boolean {
	try {
		return AppStorage.getItem(migrationFlagKey(storeKey, field)) === '1';
	} catch {
		return false;
	}
}
function markMigrated(storeKey: string, field: string): void {
	try {
		AppStorage.setItem(migrationFlagKey(storeKey, field), '1');
	} catch (error) {
		logger.warn(`Failed to persist migration flag for ${storeKey}.${field}:`, error);
	}
}
function dirtyFlagKey(storeKey: string, field: string): string {
	return `${DIRTY_FLAG_PREFIX}:${storeKey}:${field}`;
}
function isDirty(storeKey: string, field: string): boolean {
	try {
		return AppStorage.getItem(dirtyFlagKey(storeKey, field)) === '1';
	} catch {
		return false;
	}
}
function markDirty(storeKey: string, field: string): void {
	try {
		AppStorage.setItem(dirtyFlagKey(storeKey, field), '1');
	} catch (error) {
		logger.warn(`Failed to persist dirty flag for ${storeKey}.${field}:`, error);
	}
}
function clearDirty(storeKey: string, field: string): void {
	try {
		AppStorage.removeItem(dirtyFlagKey(storeKey, field));
	} catch (error) {
		logger.warn(`Failed to clear dirty flag for ${storeKey}.${field}:`, error);
	}
}

function isHttpStatus(error: unknown, status: number): boolean {
	if (error == null || typeof error !== 'object') return false;
	const candidate = (error as {status?: unknown}).status;
	return candidate === status;
}
function getHttpStatus(error: unknown): number | null {
	if (error == null || typeof error !== 'object') return null;
	const candidate = (error as {status?: unknown}).status;
	return typeof candidate === 'number' ? candidate : null;
}

export async function makeSyncedField<
	T extends object,
	F extends SyncedPreferencesField,
	M extends Message & NonNullable<SyncedPreferences[F]>,
>(store: T, config: SyncedFieldConfig<T, F, M>): Promise<void> {
	const key = store.constructor.name;
	const fieldKey = String(config.field);
	const tag = `${key}.${fieldKey}`;
	const maxEncodedBytes = config.maxEncodedBytes ?? DEFAULT_MAX_ENCODED_BYTES;

	await makePersistent(store, key, config.persist as Array<keyof T>, {
		version: config.version,
		syncAcrossTabs: config.syncAcrossTabs,
	});

	const isEnabled = (): boolean => config.enabled?.() ?? true;
	let suppressPush = false;
	let lastLocalMutationAt = 0;
	let broken = false;
	let consecutiveFailures = 0;
	let throttledUntil = 0;

	const buildMessage = (): M => create(config.schema, config.toMessage(store)) as NonNullable<SyncedPreferences[F]> as M;
	const buildEmpty = (): M => create(config.schema) as NonNullable<SyncedPreferences[F]> as M;

	const checkRoundtripStability = (candidate: M): boolean => {
		const result = verifyRoundtripStability<T, M>({
			schema: config.schema,
			store,
			toMessage: config.toMessage,
			applyMessage: config.applyMessage,
			candidate,
		});
		if (result.threw !== undefined) {
			logger.error(`${tag}: applyMessage threw during roundtrip probe — marking field broken:`, result.threw);
			return false;
		}
		if (!result.stable) {
			logger.error(
				`${tag}: roundtrip-stability check failed (toMessage → applyMessage → toMessage produced a different message). ` +
					`This indicates apply/derive feedback. Suspending pushes for this field.`,
			);
			return false;
		}
		return true;
	};

	const computeBackoff = (): number => {
		const exp = Math.min(consecutiveFailures - FAILING_THRESHOLD, 6);
		return Math.min(FAILING_BACKOFF_MAX_MS, FAILING_BACKOFF_MIN_MS * 2 ** Math.max(0, exp));
	};

	const applySnapshot = (snapshot: SyncedPreferences[F] | undefined): void => {
		if (snapshot === undefined) return;
		if (!isEnabled()) return;
		if (Date.now() - lastLocalMutationAt < RECENT_LOCAL_MUTATION_WINDOW_MS) {
			const incoming = snapshot as M;
			const local = buildMessage();
			if (!equals(config.schema, incoming, local)) {
				logger.debug(
					`${tag}: dropping server snapshot received within recent-mutation window (` +
						`${Date.now() - lastLocalMutationAt}ms since local change)`,
				);
				return;
			}
		}
		suppressPush = true;
		try {
			runInAction(() => {
				config.applyMessage(store, snapshot as M);
			});
		} finally {
			suppressPush = false;
		}
	};

	const flush = (): void => {
		if (!UserSettings.isHydrated() || !isEnabled()) return;
		if (broken) return;
		if (Date.now() < throttledUntil) return;
		const candidate = buildMessage();
		const current = UserSettings.getSubPreference(config.field);
		if (current !== undefined && equals(config.schema, current as M, candidate)) {
			clearDirty(key, fieldKey);
			return;
		}
		const encodedBytes = toBinary(config.schema, candidate).length;
		if (encodedBytes > maxEncodedBytes) {
			logger.error(
				`${tag}: payload of ${encodedBytes} bytes exceeds per-field budget of ${maxEncodedBytes}. ` +
					`Push dropped to protect synced_preferences total budget.`,
			);
			broken = true;
			return;
		}
		if (!checkRoundtripStability(candidate)) {
			broken = true;
			return;
		}
		void UserSettings.setSubPreference(config.field, candidate).then(
			() => {
				consecutiveFailures = 0;
				throttledUntil = 0;
				clearDirty(key, fieldKey);
			},
			(error) => {
				const status = getHttpStatus(error);
				if (status === 429 || isHttpStatus(error, 429)) {
					return;
				}
				if (status !== null && status >= 400 && status < 500) {
					consecutiveFailures += 1;
					logger.warn(
						`${tag}: PATCH failed with status ${status} (consecutive non-429 4xx: ${consecutiveFailures})`,
					);
					if (consecutiveFailures >= FAILING_THRESHOLD) {
						const backoff = computeBackoff();
						throttledUntil = Date.now() + backoff;
						logger.error(
							`${tag}: ${consecutiveFailures} consecutive 4xx — suspending pushes for ${Math.round(
								backoff / 1000,
							)}s`,
						);
					}
					return;
				}
				logger.error(`${tag}: failed to sync to server:`, error);
			},
		);
	};

	const reconcile = (): void => {
		if (!isEnabled()) return;
		const local = buildMessage();
		const empty = buildEmpty();
		const localHasData = !equals(config.schema, local, empty);
		const serverValue = UserSettings.getSubPreference(config.field) as M | undefined;
		const dirty = isDirty(key, fieldKey);
		const previouslyMigrated = isMigrated(key, fieldKey);
		if (!previouslyMigrated) markMigrated(key, fieldKey);

		if (serverValue === undefined) {
			if (localHasData) flush();
			else clearDirty(key, fieldKey);
			return;
		}
		const serverHasData = !equals(config.schema, serverValue, empty);
		if (!localHasData) {
			applySnapshot(serverValue);
			clearDirty(key, fieldKey);
			return;
		}
		if (!serverHasData) {
			flush();
			return;
		}
		if (equals(config.schema, local, serverValue)) {
			clearDirty(key, fieldKey);
			return;
		}
		if (config.mergeForMigration !== undefined && !previouslyMigrated) {
			const merged = create(config.schema, config.mergeForMigration(local, serverValue)) as M;
			if (!equals(config.schema, merged, local)) {
				suppressPush = true;
				try {
					runInAction(() => {
						config.applyMessage(store, merged);
					});
				} finally {
					suppressPush = false;
				}
			}
			if (!equals(config.schema, merged, serverValue)) {
				void UserSettings.setSubPreference(config.field, merged).then(
					() => clearDirty(key, fieldKey),
					(error) => logger.error(`${tag}: failed to push merged value:`, error),
				);
			} else {
				clearDirty(key, fieldKey);
			}
			return;
		}
		if (dirty) {
			flush();
			return;
		}
		applySnapshot(serverValue);
		clearDirty(key, fieldKey);
	};

	const installSteadyStateReactions = (): void => {
		reaction(
			() => (isEnabled() ? UserSettings.getSubPreference(config.field) : undefined),
			(snapshot) => applySnapshot(snapshot),
		);
		const debounceMs = config.debounceMs ?? DEFAULT_DEBOUNCE_MS;
		let pendingTimer: ReturnType<typeof setTimeout> | null = null;
		reaction(
			() => buildMessage(),
			() => {
				if (suppressPush) return;
				if (!isEnabled()) return;
				if (broken) return;
				lastLocalMutationAt = Date.now();
				markDirty(key, fieldKey);
				if (pendingTimer != null) clearTimeout(pendingTimer);
				pendingTimer = setTimeout(() => {
					pendingTimer = null;
					flush();
				}, debounceMs);
			},
		);
	};

	void when(
		() => UserSettings.isHydrated() && isEnabled(),
		() => {
			reconcile();
			installSteadyStateReactions();
		},
	);
}
